import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IncidentModels } from '../../models';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { MatDialog } from '@angular/material/dialog';
import { MatSidenavModule } from '@angular/material/sidenav';
import { orgUsersFeature } from '../../store/state/org-users/org-users.feature';
import { OrgUsersActions } from '../../store/state/org-users/org-users.actions';
import { accountsMapFeature } from '../../store/state/accounts-map/accounts-map.feature';
import { IncidentsActions } from '../../store/state/incidents/incidents.action-types';
import { IncidentsSelectors } from '../../store/state/incidents/incidents.selector-types';
import { ContactIdDbService } from '../../services/contact-id-db.service';
import { IncidentsService } from '../../services/incidents.service';
import { IncidentDetailsComponent } from '../home/components/incident-details/incident-details.component';
import {
  IncidentDetailsDialogComponent,
  IncidentDetailsDialogData,
  IncidentDetailsDialogResult,
} from '../../components/modals/incident-details-dialog/incident-details-dialog.component';
import {
  CellTemplateContext,
  DatasetTableConfig,
  OnRangeSelectedResult,
  SortChangeEvent,
  TableSortState,
  UiCalendarComponent,
  UiCalendarInputConfig,
  UiDatasetTableComponent,
  UiPreloaderComponent,
  UiModels,
  UiSearchComponent,
  UiDatetimeRangePickerModel,
  UiNoDataComponent,
  TruncateDirective,
} from '@ui';
import { FormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import {
  createReportsTableConfig,
  DEFAULT_REPORTS_SORT_STATE,
  IncidentRow,
  getNextSortState,
  getPriorityClassForRow,
  getPriorityLabelKeyForRow,
  getStatusClassForRow,
  getStatusLabelKeyForRow,
  mapIncidentsToReportRows,
  sortReportRows,
} from './reports-table.util';
import { features } from '../../consts/text.const';
import { ActivatedRoute, Router } from '@angular/router';
import { BreadcrumbService } from '@ui';
import { routerSegments } from '../../consts/routes';

const TIME_RANGE_MS = 7 * 24 * 60 * 60 * 1000;
const PAGE_SIZE = 50;
const UNASSIGNED_OPERATOR_FILTER = '__UNASSIGNED__';
const CALENDAR_INPUT_CONFIG: UiCalendarInputConfig = {
  widthPx: 156,
  heightPx: 32,
  boxSizing: 'border-box',
};
const PRIORITY_FILTER_ORDER: IncidentModels.Priority[] = [
  IncidentModels.Priority.Critical,
  IncidentModels.Priority.High,
  IncidentModels.Priority.Medium,
  IncidentModels.Priority.Low,
  IncidentModels.Priority.Info,
  IncidentModels.Priority.Administrative,
];
const STATUS_FILTER_OPTIONS: IncidentModels.Status[] = [
  IncidentModels.Status.ACTIVE,
  IncidentModels.Status.TIMEOUT,
  IncidentModels.Status.PARKED,
  IncidentModels.Status.CLOSED,
];

@Component({
  selector: 'app-reports',
  templateUrl: './reports.component.html',
  styleUrl: './reports.component.scss',
  standalone: true,
  imports: [
    FormsModule,
    MatSelectModule,
    MatSidenavModule,
    TranslatePipe,
    UiSearchComponent,
    UiCalendarComponent,
    UiDatasetTableComponent,
    UiPreloaderComponent,
    IncidentDetailsComponent,
    UiNoDataComponent,
    TruncateDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportsComponent implements OnInit {
  private readonly store$ = inject(Store);
  private readonly contactIdDbService = inject(ContactIdDbService);
  private readonly translateService = inject(TranslateService);
  private readonly incidentsService = inject(IncidentsService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly breadcrumbService = inject(BreadcrumbService);

  private readonly incidentCellTemplate =
    viewChild<TemplateRef<CellTemplateContext<IncidentRow>>>('incidentCell');
  private readonly dateCellTemplate =
    viewChild<TemplateRef<CellTemplateContext<IncidentRow>>>('dateCell');
  private readonly priorityCellTemplate =
    viewChild<TemplateRef<CellTemplateContext<IncidentRow>>>('priorityCell');
  private readonly statusCellTemplate =
    viewChild<TemplateRef<CellTemplateContext<IncidentRow>>>('statusCell');
  private readonly operatorCellTemplate =
    viewChild<TemplateRef<CellTemplateContext<IncidentRow>>>('operatorCell');
  private readonly organizationCellTemplate =
    viewChild<TemplateRef<CellTemplateContext<IncidentRow>>>(
      'organizationCell',
    );
  private readonly accountCellTemplate =
    viewChild<TemplateRef<CellTemplateContext<IncidentRow>>>('accountCell');

  public noResults = features.reports.noResults;

  private readonly dateFormatter = new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  private readonly timeFormatter = new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });

  public readonly incidents = this.store$.selectSignal(
    IncidentsSelectors.selectAllIncidents,
  );
  public readonly loading = this.store$.selectSignal(
    IncidentsSelectors.selectIncidentsIsLoading,
  );
  public readonly currentPage = this.store$.selectSignal(
    IncidentsSelectors.selectIncidentsPage,
  );
  public readonly totalIncidents = this.store$.selectSignal(
    IncidentsSelectors.selectIncidentsTotal,
  );
  public readonly sortState = signal<TableSortState>(DEFAULT_REPORTS_SORT_STATE);
  public readonly searchQuery = signal<string>('');
  public readonly selectedDateRange = signal<OnRangeSelectedResult>(
    this.getDefaultDateRange(),
  );
  public readonly selectedStatus = signal<IncidentModels.Status | null>(null);
  public readonly selectedOperator = signal<string | null>(null);
  public readonly selectedPriority = signal<IncidentModels.Priority | null>(null);
  public readonly selectedOrganization = signal<string | null>(null);
  public readonly selectedAccount = signal<string | null>(null);

  public readonly selectedIncident = signal<IncidentModels.Display | null>(null);
  public readonly incidentDetailsLoading = signal<boolean>(false);
  public readonly incidentDetailsOpen = computed(
    () => this.incidentDetailsLoading() || this.selectedIncident() !== null,
  );

  public readonly orgUsersEntities = this.store$.selectSignal(
    orgUsersFeature.selectEntities,
  );
  public readonly accountsEntities = this.store$.selectSignal(
    accountsMapFeature.selectEntities,
  );

  public readonly tableRows = computed<IncidentRow[]>(() => {
    const unknownEventLabel = String(
      this.translateService.instant('HOME.UNTITLED_EVENT'),
    );

    return mapIncidentsToReportRows(
      this.incidents(),
      this.orgUsersEntities(),
      this.accountsEntities(),
      this.contactIdDbService,
      unknownEventLabel,
      String(this.translateService.instant('HOME.UNKNOWN_USER')),
      this.dateFormatter,
      this.timeFormatter,
    );
  });

  public readonly filteredRows = computed<IncidentRow[]>(() => {
    const query = this.normalizeSearch(this.searchQuery());
    const status = this.selectedStatus();
    const priority = this.selectedPriority();
    const operator = this.selectedOperator();
    const organization = this.selectedOrganization();
    const account = this.selectedAccount();

    return this.tableRows().filter((row) => {
      if (status !== null && row.status !== status) {
        return false;
      }
      if (priority !== null && row.priority !== priority) {
        return false;
      }
      if (operator !== null) {
        const isUnassignedFilter = operator === UNASSIGNED_OPERATOR_FILTER;
        const isUnassignedRow = !row.operatorName;
        if (isUnassignedFilter && !isUnassignedRow) {
          return false;
        }
        if (!isUnassignedFilter && row.operatorName !== operator) {
          return false;
        }
      }
      if (organization !== null && row.organizationName !== organization) {
        return false;
      }
      if (account !== null && row.accountName !== account) {
        return false;
      }
      if (!query) {
        return true;
      }

      const normalizedTitle = this.normalizeSearch(row.incidentTitle);
      const normalizedSubtitle = this.normalizeSearch(row.incidentSubtitle);
      const incidentId = String(row.id);
      const incidentCode = `inc-${row.id}`.toLowerCase();
      return (
        normalizedTitle.includes(query) ||
        normalizedSubtitle.includes(query) ||
        incidentId.includes(query) ||
        incidentCode.includes(query)
      );
    });
  });

  public readonly sortedRows = computed<IncidentRow[]>(() => {
    return sortReportRows(this.filteredRows(), this.sortState());
  });

  public readonly operatorOptions = computed<string[]>(() => {
    const operatorSet = new Set<string>();
    for (const row of this.tableRows()) {
      if (row.operatorName) {
        operatorSet.add(row.operatorName);
      }
    }
    return Array.from(operatorSet).sort((left, right) =>
      left.localeCompare(right, undefined, { sensitivity: 'base' }),
    );
  });

  public readonly priorityOptions = PRIORITY_FILTER_ORDER;

  public readonly organizationOptions = computed<string[]>(() => {
    const orgSet = new Set<string>();
    for (const row of this.tableRows()) {
      if (row.organizationName) {
        orgSet.add(row.organizationName);
      }
    }
    return Array.from(orgSet).sort((left, right) =>
      left.localeCompare(right, undefined, { sensitivity: 'base' }),
    );
  });

  public readonly accountOptions = computed<string[]>(() => {
    const selectedOrganization = this.selectedOrganization();
    const accountSet = new Set<string>();
    for (const row of this.tableRows()) {
      if (!row.accountName) {
        continue;
      }
      if (
        selectedOrganization !== null &&
        row.organizationName !== selectedOrganization
      ) {
        continue;
      }
      accountSet.add(row.accountName);
    }
    return Array.from(accountSet).sort((left, right) =>
      left.localeCompare(right, undefined, { sensitivity: 'base' }),
    );
  });

  public readonly tableConfig = computed<DatasetTableConfig<IncidentRow>>(() =>
    createReportsTableConfig({
      incidentCell: this.incidentCellTemplate()!,
      dateCell: this.dateCellTemplate()!,
      priorityCell: this.priorityCellTemplate()!,
      statusCell: this.statusCellTemplate()!,
      operatorCell: this.operatorCellTemplate()!,
      organizationCell: this.organizationCellTemplate()!,
      accountCell: this.accountCellTemplate()!,
      onRowClick: (row) => this.onRowClick(row),
    }),
  );

  public ngOnInit(): void {
    this.store$.dispatch(OrgUsersActions.loadOrgUsers({ force: true }));
    this.loadIncidents();
    void this.breadcrumbService.updateBreadcrumb(this.route, this.router, [
      { name: 'HOME.TITLE', route: routerSegments.home },
      { name: 'REPORTS.TITLE' },
    ]);
  }

  public onLoadMore(): void {
    if (this.loading()) {
      return;
    }

    const total = this.totalIncidents();
    if (total > 0 && this.incidents().length >= total) {
      return;
    }

    const nextPage = this.currentPage() + 1;
    this.loadIncidents(nextPage, true);
  }

  public onSearchQueryChange(value: string): void {
    this.searchQuery.set(value ?? '');
  }

  public onDateRangeChange(range: OnRangeSelectedResult): void {
    if (!range) {
      return;
    }
    this.selectedDateRange.set(range);
    this.loadIncidents();
  }

  public onStatusFilterChange(status: IncidentModels.Status | null): void {
    this.selectedStatus.set(status);
  }

  public onOperatorFilterChange(operator: string | null): void {
    this.selectedOperator.set(operator);
  }

  public onPriorityFilterChange(priority: IncidentModels.Priority | null): void {
    this.selectedPriority.set(priority);
    this.loadIncidents();
  }

  public onOrganizationFilterChange(organization: string | null): void {
    this.selectedOrganization.set(organization);
    if (organization !== null) {
      const account = this.selectedAccount();
      if (
        account !== null &&
        !this.tableRows().some(
          (row) =>
            row.organizationName === organization && row.accountName === account,
        )
      ) {
        this.selectedAccount.set(null);
      }
    }
  }

  public onAccountFilterChange(account: string | null): void {
    this.selectedAccount.set(account);
  }

  public getStatusFilterLabelKey(status: IncidentModels.Status): string {
    return IncidentModels.getStatusTranslationKey(status);
  }

  public getPriorityFilterLabelKey(priority: IncidentModels.Priority): string {
    return IncidentModels.getPriorityTranslationKey(priority);
  }

  public onSortChange(event: SortChangeEvent): void {
    this.sortState.set(getNextSortState(this.sortState(), event));
  }

  public getPriorityClass(row: IncidentRow): string {
    return getPriorityClassForRow(row);
  }

  public getPriorityLabelKey(row: IncidentRow): string {
    return getPriorityLabelKeyForRow(row);
  }

  public getStatusClass(row: IncidentRow): string {
    return getStatusClassForRow(row);
  }

  public getStatusLabelKey(row: IncidentRow): string {
    return getStatusLabelKeyForRow(row);
  }

  public readonly statusFilterOptions = STATUS_FILTER_OPTIONS;
  public readonly inputStyles = UiModels.UIInputStyle;
  public readonly calendarInputConfig = CALENDAR_INPUT_CONFIG;
  public readonly unassignedOperatorFilter = UNASSIGNED_OPERATOR_FILTER;

  public onRowClick(row: IncidentRow): void {
    this.incidentDetailsLoading.set(true);
    this.selectedIncident.set(null);
    this.incidentsService
      .getIncidentById(row.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (incident) => {
          const display: IncidentModels.Display = {
            ...incident,
            thumbnailUrl: IncidentModels.getThumbnailUrlFromLastImages(
              incident.lastImages,
            ),
            title: this.contactIdDbService.getHighestPriorityTitle(
              incident.policyCodes,
            ),
          };
          this.selectedIncident.set(display);
          this.incidentDetailsLoading.set(false);
        },
        error: () => {
          this.selectedIncident.set(null);
          this.incidentDetailsLoading.set(false);
        },
      });
  }

  public onCloseDetails(): void {
    this.selectedIncident.set(null);
    this.incidentDetailsLoading.set(false);
  }

  public onMaximizeIncident(): void {
    const incident = this.selectedIncident();
    if (!incident) return;

    const dialogRef = this.dialog.open<
      IncidentDetailsDialogComponent,
      IncidentDetailsDialogData,
      IncidentDetailsDialogResult
    >(IncidentDetailsDialogComponent, {
      data: { incident },
      panelClass: 'incident-details-dialog',
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === 'status-changed') {
        this.loadIncidents();
      }
      if (result !== 'default-view') {
        this.onCloseDetails();
      }
    });
  }

  public onIncidentStatusChanged(): void {
    this.loadIncidents();
  }

  private loadIncidents(page = 1, append = false): void {
    const { start, end } = this.getTimeRange();
    const selectedPriority = this.selectedPriority();
    const request: IncidentModels.SearchRequest = {
      start,
      end,
      priority: selectedPriority ? [selectedPriority] : undefined,
      page,
      size: PAGE_SIZE,
    };

    this.store$.dispatch(IncidentsActions.loadIncidents({ request, append }));
  }

  private getTimeRange(): { start: number; end: number } {
    const selectedRange = this.selectedDateRange();
    if (selectedRange.type === UiModels.UiCalendarPickerType.ABSOLUTE) {
      const absoluteStart = this.getRangeBoundAsTs(selectedRange.absolute.start);
      const absoluteEnd = this.getRangeBoundAsTs(selectedRange.absolute.end);
      return {
        start: Math.min(absoluteStart, absoluteEnd),
        end: Math.max(absoluteStart, absoluteEnd),
      };
    }

    const now = Date.now();
    const relative = selectedRange.relative;
    if (relative?.value == null || relative.value <= 0) {
      return { start: now - TIME_RANGE_MS, end: now };
    }

    const unitMsMap: Record<UiDatetimeRangePickerModel.CustomUnit, number> = {
      [UiDatetimeRangePickerModel.CustomUnit.minutes]: 60 * 1000,
      [UiDatetimeRangePickerModel.CustomUnit.hours]: 60 * 60 * 1000,
      [UiDatetimeRangePickerModel.CustomUnit.days]: 24 * 60 * 60 * 1000,
      [UiDatetimeRangePickerModel.CustomUnit.weeks]: 7 * 24 * 60 * 60 * 1000,
    };
    const unitMs = unitMsMap[relative.unit];
    return { start: now - relative.value * unitMs, end: now };
  }

  private getRangeBoundAsTs(value: string | number): number {
    if (typeof value === 'number') {
      return value;
    }
    const parsed = Date.parse(value);
    if (Number.isNaN(parsed)) {
      return Date.now();
    }
    return parsed;
  }

  private normalizeSearch(value: string | null | undefined): string {
    return (value ?? '').trim().toLowerCase();
  }

  private getDefaultDateRange(): OnRangeSelectedResult {
    const now = Date.now();
    return {
      type: UiModels.UiCalendarPickerType.RELATIVE,
      relative: {
        unit: UiDatetimeRangePickerModel.CustomUnit.days,
        value: 7,
      },
      absolute: {
        start: now - TIME_RANGE_MS,
        end: now,
      },
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
  }

}